/**
 * Seed a small set of realistic journal entries on first mount so the
 * Trial Balance / GL Detail surfaces have something to render in beta.
 *
 * Idempotent: only runs ONCE per browser session. Without the sessionStorage
 * sentinel, voiding every JE would re-trigger the seed because the journal
 * would briefly look empty again — a major footgun before Supabase wiring.
 */
import { useEffect, useRef } from "react";
import { useGeneralLedger } from "./GeneralLedgerContext";

const SEED_FLAG = "koredor.gl.seeded.v1";
const isDev = import.meta.env.DEV;

export function useSeedGeneralLedger() {
  const { journalEntries, postJournal } = useGeneralLedger();
  const ran = useRef(false);

  useEffect(() => {
    if (!isDev) return;
    if (ran.current) return;
    if (journalEntries.length > 0) { ran.current = true; return; }
    if (typeof window !== "undefined" && window.sessionStorage.getItem(SEED_FLAG)) {
      ran.current = true;
      return;
    }
    ran.current = true;
    if (typeof window !== "undefined") window.sessionStorage.setItem(SEED_FLAG, "1");

    // ── Opening balances ──
    postJournal({
      postingDate: "2026-01-01",
      description: "Opening balances",
      source: "adjustment",
      lines: [
        { accountId: "1000", amount: 125000, side: "debit", memo: "Operating cash open" },
        { accountId: "1010", amount: 32000, side: "debit", memo: "Payroll cash open" },
        { accountId: "1500", amount: 88000, side: "debit", memo: "Trucks + equipment" },
        { accountId: "3000", amount: 245000, side: "credit", memo: "Owner equity" },
      ],
    });

    // ── AR invoice (job revenue + AR) ──
    postJournal({
      postingDate: "2026-06-02",
      description: "Invoice #1042 — Martinez bathroom remodel",
      source: "ar_invoice",
      sourceRef: { kind: "invoice", id: "INV-1042" },
      lines: [
        { accountId: "1100", amount: 18500, side: "debit", customerId: "cust_martinez", memo: "INV-1042" },
        { accountId: "2200", amount: 18500, side: "credit", customerId: "cust_martinez", jobId: "job_001", memo: "Deferred revenue (invoice issued)" },
      ],
    });

    // ── AP bill into WIP ──
    postJournal({
      postingDate: "2026-06-05",
      description: "Vendor bill — Home Depot PO-882 (Bathroom tile)",
      source: "ap_bill",
      sourceRef: { kind: "bill", id: "BILL-882" },
      lines: [
        { accountId: "1200", amount: 4250, side: "debit", jobId: "job_001", tradeType: "Bathroom" },
        { accountId: "2000", amount: 4250, side: "credit", vendorId: "vend_homedepot" },
      ],
    });

    // ── AR cash receipt ──
    postJournal({
      postingDate: "2026-06-10",
      description: "Customer payment — Martinez INV-1042 deposit",
      source: "ar_cash_receipt",
      lines: [
        { accountId: "1000", amount: 9250, side: "debit", memo: "ACH" },
        { accountId: "1100", amount: 9250, side: "credit", customerId: "cust_martinez" },
      ],
    });

    // ── AP payment ──
    postJournal({
      postingDate: "2026-06-12",
      description: "Pay vendor — Home Depot BILL-882",
      source: "ap_payment",
      lines: [
        { accountId: "2000", amount: 4250, side: "debit", vendorId: "vend_homedepot" },
        { accountId: "1000", amount: 4250, side: "credit" },
      ],
    });

    // ── CC expense (meals) ──
    postJournal({
      postingDate: "2026-06-14",
      description: "Lunch w/ supplier — Amex",
      source: "manual",
      lines: [
        { accountId: "6600", amount: 112, side: "debit", memo: "Vendor meeting" },
        { accountId: "2100", amount: 112, side: "credit" },
      ],
    });

    // ── Payroll burden ──
    postJournal({
      postingDate: "2026-06-15",
      description: "Pay run — Crew week ending 6/14",
      source: "payroll",
      lines: [
        { accountId: "1200", amount: 6800, side: "debit", jobId: "job_001", tradeType: "Bathroom", memo: "Direct labor" },
        { accountId: "6000", amount: 2400, side: "debit", memo: "Office payroll" },
        { accountId: "6010", amount: 1450, side: "debit", memo: "Burden" },
        { accountId: "1010", amount: 10650, side: "credit", memo: "Payroll cash out" },
      ],
    });

    // ── Job complete — recognize revenue + release WIP ──
    postJournal({
      postingDate: "2026-06-20",
      description: "Job complete — Martinez bathroom: recognize revenue, release WIP to COGS",
      source: "wip_release",
      sourceRef: { kind: "job_complete", id: "job_001" },
      lines: [
        // Recognize revenue (move from Deferred Revenue → Revenue)
        { accountId: "2200", amount: 18500, side: "debit", customerId: "cust_martinez", jobId: "job_001", memo: "Recognize revenue at job complete" },
        { accountId: "4030", amount: 18500, side: "credit", tradeType: "Bathroom", jobId: "job_001", memo: "Bathroom revenue" },
        // Release WIP → COGS
        { accountId: "5020", amount: 4250, side: "debit", jobId: "job_001", tradeType: "Bathroom", memo: "Material to COGS" },
        { accountId: "5520", amount: 6800, side: "debit", jobId: "job_001", tradeType: "Bathroom", memo: "Labor to COGS" },
        { accountId: "1200", amount: 11050, side: "credit", jobId: "job_001", tradeType: "Bathroom", memo: "Release WIP" },
      ],
    });
  }, [journalEntries.length, postJournal]);
}
